import { useMemo } from 'react';

import type { V1CodeManifest } from '@/generated/hooks/jeeves/models';
import type {
  IFunctionCallEvent,
  INodeStartedEvent,
} from '@/modules/automation-run/hooks/useRunJournalEvents';
import {
  type IResolvedExternalCall,
  resolveExternalCalls,
} from '@/modules/automation-run/utils/external-call-visibility';
import type { TResolvedValue } from '@/modules/outputs/types';
import {
  extractOutputsValue,
  resolveCommonValue,
} from '@/modules/outputs/utils';
import { computeTimestampDuration } from '@/shared/utils';

import type { TStepRunStatus } from '../utils/step-run-status';

/** What one step actually did on a run: its real inputs, output and timing. */
export interface IStepRunIO {
  inputs: [string, TResolvedValue][];
  output: TResolvedValue | null;
  durationLabel: string | null;
  /** Journal start time, present only while this step is executing. */
  startedTimestamp?: string;
  /** The step's external book calls with their outputs; empty for none. */
  externalCalls?: IResolvedExternalCall[];
  /**
   * The run's error message, set only on the step that failed or is awaiting
   * guidance. Undefined for every other step, and for a run with neither
   * state.
   */
  errorText?: string;
}

/** Stable empty map, so a manifest-less render keeps one reference. */
const NO_STEP_IO: ReadonlyMap<number, IStepRunIO> = new Map();

/** Params for {@link useManifestStepIO}. */
interface IUseManifestStepIOParams {
  manifest: V1CodeManifest | undefined;
  /** Finished function-call steps from the run's bishop journal. */
  events: IFunctionCallEvent[];
  /** Statuses for the same run, used to locate the step an error belongs to. */
  statuses: ReadonlyMap<number, TStepRunStatus>;
  /** `RunState.failed`/`awaiting_guidance` description, when either is set. */
  errorDescription?: string;
  /** Calls buffered against a step that never completed. */
  runningStepExternalCalls?: IFunctionCallEvent[];
  runningStep?: INodeStartedEvent | null;
}

/**
 * Per-step inputs, output, duration and external calls for a run, keyed on the
 * manifest's 1-based step number.
 *
 * Finished calls match by entry-function name; active calls match their manifest call site.
 */
export function useManifestStepIO({
  manifest,
  events,
  statuses,
  errorDescription,
  runningStepExternalCalls,
  runningStep,
}: IUseManifestStepIOParams): ReadonlyMap<number, IStepRunIO> {
  const steps = useMemo(
    () => (manifest?.steps ?? []).filter(step => step.entry_function?.name),
    [manifest],
  );

  // Keyed only on the events they come from, so a buffered external call can't
  // hand every step's `output` a fresh reference and remount its renderer.
  const baseStepIO = useMemo(() => {
    if (!manifest) return NO_STEP_IO;
    const eventByFunctionName = new Map<string, IFunctionCallEvent>();
    for (const event of events) {
      eventByFunctionName.set(event.functionName, event);
    }

    const result = new Map<number, IStepRunIO>();
    for (const step of steps) {
      const fn = step.entry_function?.name;
      if (step.number == null || !fn) continue;
      const event = eventByFunctionName.get(fn);
      if (!event) continue;
      result.set(step.number, {
        inputs: event.inputs
          ? (Object.entries(extractOutputsValue(event.inputs)) as [
              string,
              TResolvedValue,
            ][])
          : [],
        output:
          event.returnValue != null
            ? resolveCommonValue(event.returnValue)
            : null,
        durationLabel: computeTimestampDuration(
          event.startedTimestamp,
          event.timestamp,
        ),
        externalCalls: resolveExternalCalls(event.externalCalls),
      });
    }
    return result;
  }, [manifest, steps, events]);

  // Active or interrupted steps have no completed call from which to read their I/O.
  return useMemo(() => {
    // The executing node's start byte falls inside the call site of the manifest step it belongs to.
    const startByte = Number(runningStep?.codeLocation?.start_byte);
    const runningStepNumber = runningStep
      ? steps.find(
          step =>
            step.number != null &&
            statuses.get(step.number) === 'running' &&
            startByte >= Number(step.call_location?.start_byte) &&
            startByte < Number(step.call_location?.end_byte),
        )?.number
      : undefined;
    if (!errorDescription && runningStepNumber == null) return baseStepIO;
    const merged = new Map(baseStepIO);
    // Inputs/output only arrive on completion; the start time drives the live elapsed badge.
    if (runningStepNumber != null && runningStep) {
      merged.set(runningStepNumber, {
        inputs: [],
        output: null,
        durationLabel: null,
        startedTimestamp: runningStep.startedTimestamp,
        externalCalls: resolveExternalCalls(runningStepExternalCalls),
      });
    }
    if (!errorDescription) return merged;
    // The failed/paused step often never completes its call, so attach the error to it here.
    const erroredStepNumber = [...statuses.entries()].find(
      ([, status]) => status === 'failed' || status === 'paused',
    )?.[0];
    if (erroredStepNumber == null) return merged;

    const existing = baseStepIO.get(erroredStepNumber);
    merged.set(erroredStepNumber, {
      inputs: existing?.inputs ?? [],
      output: existing?.output ?? null,
      durationLabel: existing?.durationLabel ?? null,
      // A step that dies mid-flight never completes, so its calls are still
      // buffered rather than attached — and they are the diagnostic ones.
      externalCalls:
        existing?.externalCalls ??
        resolveExternalCalls(runningStepExternalCalls),
      errorText: errorDescription,
    });
    return merged;
  }, [
    baseStepIO,
    steps,
    statuses,
    errorDescription,
    runningStepExternalCalls,
    runningStep,
  ]);
}
